import { IViolation, IOwnedViolation } from './../base/annotation';
import { Activity, ActivityScan, Link } from './activity';
import { Parameter } from '../base/parameter';
import { IModel, Collection } from '../base/annotation';
import { Metamodel } from '../base/model';
import { shellStringResources } from '../../Resources/ShellStringResources';
import { sanitizeAndFormatString } from '../../shared/utils/string.util';
import { IParameterizable } from '../../interfaces/IParameterizable';
import { Folder } from '../folder/folder';
import { OwnedSet } from '../base/ownedset';
import { PipelinePolicy } from './pipelinepolicy';
import { TaggableNamed } from '../base/taggable-named';
import { Owned, Named } from '../base/named';
import { Variable } from '../base/variable';
import { ParameterConfig } from './parameter-config';
import { getAllActivityNamesWithinPipeline, countActivitiesInPipeline } from './pipeline.utils';
import { Subject, Observable, of } from 'rxjs';
import { ActionConstants } from '../../constants/action-constants';
import { getGeneralNamePatterns } from '../../shared/utils/validation.util';
import { TriggerSettings } from '../trigger/trigger-settings';
import { ModelConstants } from '../model-constants';
import { GlobalVariables } from '../../constants/globals';
import { Constants } from '../../shared/shared-constants';
import { LibraryVariable } from '../base/libraryvariable';
import { Dataset } from '../dataset/dataset';
import { LinkedService } from '../linkedservice/linkedservice';
// import { ADF_CI_ORIGIN, ADF_TEST_ORIGIN } from 'app/services/globals'; // Breaks the build?

const ADF_TEST_ORIGIN: string = 'https://atlas-preview.cloudapp.net';
const ADF_CI_ORIGIN: string = 'https://madrid-ci.azure.com';

export interface IDataDependencyTypeProperties {
    timeout?: string;
    sleep?: number;
    dataset?: {
        referenceName: string;
        type: string;
    };
    datasetSettings?: {
        type: string;
        [key: string]: unknown;
    };
    storeSettings?: {
        type: string;
        [key: string]: unknown;
    };
    formatSettings?: {
        type: string;
        [key: string]: unknown;
    };
}

export interface IDataDependency {
    type?: 'data';
    name: string;
    typeProperties: IDataDependencyTypeProperties;
}

// V4.1 unified label-based dependency design
export interface IDependencyParentReference {
    referenceName: string;
    type?: string;
    offset?: string;
    size?: string;
}

export interface IDependencyMatchOn {
    type: 'label' | 'windowCoverage';
    key?: string;
    windowSize?: string;
    anchor?: string;
}

export interface IDependencyRequire {
    key: string;
    op: string;
    value: string | number | string[];
}

export interface IPipelineDependencyTypeProperties {
    dependencyName: string;
    dependsOn: IDependencyParentReference[];
    matchOn: IDependencyMatchOn;
    require?: IDependencyRequire;
}

export interface IPipelineDependency {
    type?: 'pipeline';
    typeProperties: IPipelineDependencyTypeProperties;
}

export type IDependency = IDataDependency | IPipelineDependency;

enum Visitation {
    GO,
    SKIP,
    STOP
}

export interface IExtendedActivity extends Activity {
    dataset?: Dataset;
    sourceDataset?: Dataset;
    sinkDataset?: Dataset;
    linkedService?: LinkedService;
    pipeline?: Pipeline; // ForEachActivity & UntilActivity
    defaultPipeline?: Pipeline; // SwitchActivity
    cases?: Array<{ casePipeline: Pipeline, value: string }>; // SwitchActivity
    ifFalseActivityPipeline?: Pipeline; // IfConditionActivity
    ifTrueActivityPipeline?: Pipeline; // IfConditionActivity
}

@Metamodel.model({
    name: ModelConstants.pipeline,
    display: [shellStringResources.Pipeline, shellStringResources.Pipelines],
    reusable: true,
    referable: true,
    constraints: {
        name: {
            required: true,
            patterns: !GlobalVariables.isTrident ? getGeneralNamePatterns(shellStringResources.Pipeline) : [],
            uniqueModelName: true
        }
    },
    validator: Pipeline.prototype.validate,
    tags: { gitValidator: Pipeline.prototype.gitValidate },
    accessPublishConstraint: {
        writeResource: {
            action: ActionConstants.pipelinesWrite
        },
        deleteResource: {
            action: ActionConstants.pipelinesDelete
        }
    }
})
export class Pipeline extends TaggableNamed implements IParameterizable {

    public static readonly MODEL: IModel;
    public hasLegacyDependency?: boolean;
    public migrationEligibility?: 'valid' | 'warning' | 'invalid';
    private _actLegacyMapping: Map<string, boolean> = new Map<string, boolean>();

    @Metamodel.attribute({
        type: Activity.MODEL,
        collection: Collection.Set,
        constraint: {
            validator: Pipeline.validateActivities
        }
    })
    public readonly activities = OwnedSet.create<Activity>(this, {

        added: (...items: Activity[]) => {
            items = items;
        },

        removed: (...activities: Activity[]) => {
            for (const activity of activities) {
                // Get rid of the links
                // Remove links into me
                for (const link of activity.fromLinks) {
                    const toLinks = link.fromActivity.toLinks;
                    toLinks.delete(link); // Should exist!
                }
                // Remove links out of me
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                for (const link of activity.toLinks) {
                    const fromLinks = link.toActivity.fromLinks;
                    fromLinks.delete(link); // Should exist!
                }
            }
        }
    });

    /* Below four properties are only used in 5x5 and are only relevant for container activities:
    / In 5x5, for container activities we care about the first row activities (i.e the first row in the canvas) of each case pipeline
    / Maintain a list and update accordingly when the case pipeline is updated
    / This helps improve canvas rendering performance
    */
    public get isNestedPipeline(): boolean {
        return this.owner && this.owner instanceof Activity;
    }

    @Metamodel.attribute({
        transient: true
    })
    public pipelineHasMoreThan1RowofActivites = false;

    @Metamodel.attribute({
        transient: true
    })
    public firstRowActivitiesNeedRecalculation = true;

    @Metamodel.attribute({
        type: Activity.MODEL,
        collection: Collection.Array,
        transient: true
    })
    public firstRowActivitiesOnCanvas: Activity[] = [];

    @Metamodel.attribute({
        type: TriggerSettings.MODEL
    })
    public triggerSettings: TriggerSettings;

    @Metamodel.attribute({
        type: Parameter.MODEL,
        collection: Collection.Set,
        constraint: {
            unique: true,
            validator: Pipeline.validateParameters
        }
    })
    public readonly parameters = OwnedSet.create<Parameter>(this);

    @Metamodel.attribute({
        display: shellStringResources.Concurrency,
        constraint: {
            required: false,
            whole: true,
            minimum: 1,
            maximum: 50
        }
    })
    public concurrency?: number;

    @Metamodel.attribute({
        type: PipelinePolicy.MODEL
    })
    public policy = Named.create<PipelinePolicy>(PipelinePolicy.MODEL, this);

    @Metamodel.attribute({
        type: Variable.MODEL,
        collection: Collection.Set,
        constraint: {
            unique: true
        }
    })
    public readonly variables = OwnedSet.create<Variable>(this);

    @Metamodel.attribute({
        type: LibraryVariable.MODEL,
        collection: Collection.Set,
        constraint: {
            unique: true,
            validator: Pipeline.validateLibraryVariables
        }
    })
    public readonly libraryVariables = OwnedSet.create<LibraryVariable>(this);

    public runDimensions: ParameterConfig[] = [];

    public dependencies: IDependency[] = [];

    public dependencyConnectionId?: string;

    public lastPipelineRunId: string;

    public folder: Folder = Owned.create(Folder.MODEL, this);

    public isRunning: boolean = false;

    public lastModifiedByObjectId: string;

    public lastModifiedByPrincipalName: string;

    public dataFactoryId: string;

    public showDEPWarning: boolean = false;

    public showSapCdcWarning: boolean = false;

    public showSharePointSpnWarning: boolean = false;

    public sharePointSpnLinkedService?: LinkedService;

    // for pipelines created from gallery templates, we need to track the source template
    public documentation: string;
    public sourceTemplate: string;
    public isMainPipeline: boolean = false;
    public isCustomTemplate: boolean = false;
    public onActivityAdded: Observable<Activity>;

    private _onActivityAdded = new Subject<Activity>();

    constructor(name?: string, description?: string) {
        super(name, description);
        this.onActivityAdded = this._onActivityAdded.asObservable();
    }

    public static validateParameters(pipeline: Pipeline): IViolation[] | undefined {
        const violations: IViolation[] = [];
        pipeline.parameters.forEach((parameter) => {
            if (GlobalVariables.strictParamValidation && !Constants.alphaNumericUnderscoreValidation.test(parameter.name)) {
                violations.push({
                    context: parameter,
                    message: () => sanitizeAndFormatString(shellStringResources.Validator_NamePattern_All_AlphanumericUnderscore, parameter.name),
                    messageType: 'invalid',
                    logDetails: {
                        functionName: 'Pipeline.validateParameters',
                        errorType: 'Pattern'
                    }
                });
            }
        });
        return violations;
    }

    private static validateActivities(pipeline: Pipeline): IViolation[] | undefined {
        if (pipeline.isFakeEntityCreatedFromReference) {
            return undefined;
        }
        const activityOccursViolations = Pipeline.validateActivityOccurs(pipeline);
        const noDuplicateNamesViolations = Pipeline.validateNoDuplicateNamedNestedActivities(pipeline);
        const maxNumActivitiesViolation = Pipeline.validateMaxNumberActivites(pipeline);
        const allViolations = [...activityOccursViolations, ...noDuplicateNamesViolations, ...maxNumActivitiesViolation];
        return allViolations.length === 0 ? undefined : allViolations;
    }

    private static validateActivityOccurs(pipeline: Pipeline): IViolation[] {
        const violations: IViolation[] = [];
        const isIfActivity = pipeline.owner && Metamodel.modelOf(pipeline.owner).name === 'IfCondition';
        const isSwitchDefaultPipeline = pipeline.name === `${pipeline.owner?.named?.name}${ModelConstants.switchActivityDefaultCase}`;
        const isSwitchCasePipeline = pipeline.owner && Metamodel.modelOf(pipeline.owner).name === 'ContainerActivityCase';
        if (!isIfActivity && !isSwitchCasePipeline && !isSwitchDefaultPipeline && !(GlobalVariables.isTrident && !pipeline.owner) && pipeline.activities.size === 0) {
            const modelName = pipeline.owner ? Metamodel.modelOf(pipeline.owner).display[0] || Metamodel.modelOf(pipeline.owner).name : shellStringResources.Pipeline;
            const entityName = pipeline.owner?.named ? pipeline.owner.named.name : pipeline.name;

            violations.push(
                {
                    context: pipeline.owner || pipeline,
                    message: () => sanitizeAndFormatString(shellStringResources.OccursValidatorMessageMinOne, modelName, entityName, shellStringResources.Activity),
                    messageType: 'missing',
                    logDetails: {
                        functionName: 'Pipeline.validateActivityOccurs',
                        errorType: 'Required'
                    }
                }
            );
        }
        // enforce custom case has at least one activity. allow default case to have no activities
        if (isSwitchCasePipeline && pipeline.activities.size === 0) {
            const modelName = pipeline.owner?.owner ? Metamodel.modelOf(pipeline.owner.owner).display[0] || Metamodel.modelOf(pipeline.owner.owner).name : shellStringResources.Pipeline;
            const entityName = pipeline.owner ? pipeline.owner.named.name : pipeline.name;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const caseName = (pipeline.owner as any)?.value;

            violations.push(
                {
                    context: pipeline.owner || pipeline,
                    message: () => sanitizeAndFormatString(shellStringResources.OccursValidatorMessageMinOneSwitch, modelName, entityName, shellStringResources.Activity, caseName),
                    messageType: 'missing',
                    logDetails: {
                        functionName: 'Pipeline.validateActivityOccurs',
                        errorType: 'Required',
                        details: {
                            activityType: 'Switch'
                        }
                    }
                }
            );
        }
        return violations;
    }

    private static validateNoDuplicateNamedNestedActivities(pipeline: Pipeline): IViolation[] {
        const activityNames = getAllActivityNamesWithinPipeline(pipeline);
        return Pipeline.getNoneUniqueNameViolations(activityNames, pipeline);
    }

    private static validateMaxNumberActivites(pipeline: Pipeline): IViolation[] {
        const violations: IViolation[] = [];

        if (window.location.origin === ADF_CI_ORIGIN || window.location.origin === ADF_TEST_ORIGIN) {
            return violations;
        }

        const activityCount = countActivitiesInPipeline(pipeline);
        if (activityCount > GlobalVariables.maxActivityLimit) {
            violations.push({
                context: pipeline.owner || pipeline,
                message: () => sanitizeAndFormatString(shellStringResources.MaxNumberOfActivitiesError, pipeline.name, GlobalVariables.maxActivityLimit.toString()),
                messageType: 'invalid',
                logDetails: {
                    functionName: 'Pipeline.validateMaxNumberActivites',
                    errorType: 'MaxSize',
                    details: { activityCount }
                }
            });
        }
        return violations;
    }

    private static getNoneUniqueNameViolations(names: string[], context: Pipeline): IViolation[] {
        const violations: IViolation[] = [];
        names.sort();
        for (let i = 0; i < names.length - 1; ++i) {
            if (names[i] === names[i + 1]) {
                const upperLevelOwnerName = context.conceptOwner ? context.conceptOwner.named.name : context.name;
                const violationMessageString = sanitizeAndFormatString(shellStringResources.ValidatorMessageNoneUniqueNestedNames, names[i], upperLevelOwnerName);
                violations.push({
                    context,
                    message: () => violationMessageString,
                    messageType: 'invalid',
                    logDetails: {
                        functionName: 'Pipeline.getNoneUniqueNameViolations',
                        errorType: 'DuplicatedName'
                    }
                });
                while (i < names.length - 1 && names[i] === names[i + 1]) {
                    ++i;
                }
            }
        }
        return violations;
    }

    private static validateLibraryVariables(pipeline: Pipeline): IViolation[] | undefined {
        const violations: IViolation[] = [];
        pipeline.libraryVariables.forEach((variable) => {
            if (GlobalVariables.strictParamValidation && !Constants.alphaNumericUnderscoreValidation.test(variable.name)) {
                violations.push({
                    context: variable,
                    message: () => sanitizeAndFormatString(shellStringResources.Validator_NamePattern_All_AlphanumericUnderscore, variable.name),
                    messageType: 'invalid',
                    logDetails: {
                        functionName: 'Pipeline.validateLibraryVariables',
                        errorType: 'Pattern'
                    }
                });
            }
        });
        return violations;
    }

    public clear(): void {
        super.clear();
        this.activities.clear();
        this.parameters.clear();
    }

    /*
        Add a activity and optionally insert at the link
    */
    public addActivity(activity: Activity, inbetween?: Link, refreshView?: boolean): Pipeline {
        this.activities.add(activity);
        if (null != inbetween) {
            this.removeLink(inbetween);
            inbetween.fromActivity.linkTo(activity, inbetween.criteria);
            activity.linkTo(inbetween.toActivity, inbetween.criteria);
        }
        if (refreshView) {
            this._onActivityAdded.next(activity);
        }
        return this;
    }

    public removeLink(link: Link): void {
        link.fromActivity.toLinks.delete(link);
        link.toActivity.fromLinks.delete(link);
    }

    public getDependencies(startActivity: Activity): Activity[] {
        const fromActivities: Activity[] = [];
        this.traverse(startActivity, false, (activity: Activity) => {
            fromActivities.push(activity);
            return Visitation.GO;
        });
        return fromActivities;
    }

    public getDependants(startActivity: Activity): Activity[] {
        const toActivities: Activity[] = [];
        this.traverse(startActivity, true, (activity: Activity) => {
            toActivities.push(activity);
            return Visitation.GO;
        });
        return toActivities;
    }

    public depthFirstScan(startActivities: Activity[]): Activity[][] {
        const columns = new Array<Activity[]>();
        columns[0] = startActivities;
        // TODO...For layout
        return columns;
    }

    /*
     * Works because it is single threaded and blocking call
     * Given a activity tells all the activities it depends on
    */
    public traverse(startActivity: Activity, forward: boolean,
        visit: (activity: Activity, linkedFrom?: Activity) => Visitation): void {
        // Reset visited flags
        for (const activity of this.activities) {
            activity._visited = 0;
        }

        // Finding in the set will slow things down.
        // We need a map and we will use the activity name to build it(assuming it is unique)
        // Else we can manufacture a id from a running set which will be faster
        const scannedActivities = new Set<Activity>();
        scannedActivities.add(startActivity);
        startActivity._visited = 1;
        while (scannedActivities.size > 0) {
            for (const scanActivity of scannedActivities) {
                scannedActivities.delete(scanActivity);

                for (const link of forward ? scanActivity.toLinks : scanActivity.fromLinks) {
                    const theActivity = forward ? link.toActivity : link.fromActivity;
                    if (theActivity._visited === 1) {
                        continue;
                    }
                    switch (visit(theActivity, scanActivity)) {
                        case Visitation.STOP: return;
                        case Visitation.SKIP: continue;
                        default:
                    }
                    scannedActivities.add(theActivity);
                    theActivity._visited = 1;
                }

            }
        }
    }

    /*
        If a link is attemped from activity to toActivity would that cause a cycle
    */
    public canCycle(fromActivity: Activity, toActivity: Activity): Activity | undefined {
        const link = new Link(fromActivity, toActivity);
        return this.cycleAt((activity) => {
            if (activity !== fromActivity) {
                return activity.toLinks;
            } else {
                const aset = new Set<Link>();
                activity.toLinks.forEach((value) => aset.add(value));
                aset.add(link);
                return aset;
            }
        });
    }
    /*
        Returns the first activity where a cycle is seen. undefined otherwise
    */
    public cycleAt(provider?: (activity: Activity) => Set<Link>): Activity | undefined {
        if (this.activities.size === 0) {
            return undefined;
        }
        // Reset visited flags
        // Lets just start on the one that do not have any fromLinks
        for (const activity of this.activities) {
            activity._visited = ActivityScan.UNSEEN;
        }

        // To do depth first all children go in first. We have no stack
        // Lets use a array to simulate one albeit at the expense of moving things around
        // TO DO: LinkedList with items?
        for (const activity of this.activities) {
            const inCycleActivity = this.dfsCycleScan(activity, provider);
            if (inCycleActivity) {
                return inCycleActivity;
            }
        }

        return undefined;
    }

    public uniqueName(activityType: IModel, prefix?: string, otherNamesToFilter: string[] = []): string {
        if (!prefix) {
            prefix = activityType.name;
        }

        const root = this.rootOwner as Pipeline;
        const allActivityNames = getAllActivityNamesWithinPipeline(root).concat(otherNamesToFilter);
        const regex = new RegExp('^' + prefix + '[0-9]+$');
        const sameActivityTypeNames = allActivityNames.filter((name: string) => name.match(regex));
        // eslint-disable-next-line radix
        const activityNumbersUsed = sameActivityTypeNames.map((name: string) => Number.parseInt(name.slice(prefix.length)));
        const newActivityNumber = Math.max(0, ...activityNumbersUsed) + 1;
        return prefix.concat(newActivityNumber.toString());
    }

    public isUniqueName(name: string, renamedActivity?: Activity): boolean {
        if (!name) {
            return false;
        }

        for (const activity of this.activities) {
            if (activity.name && activity !== renamedActivity &&
                activity.name.toUpperCase() === name.toUpperCase()) {
                return false;
            }
        }

        return true;
    }

    public validate(): IViolation[] | undefined {
        const activity = this.cycleAt();
        if (!activity) {
            return undefined;
        }
        const violation: IViolation = {
            context: activity,
            message: () => sanitizeAndFormatString(shellStringResources.PipelineValidationMessage_Cyclic, this.name, activity.name),
            messageType: 'invalid',
            logDetails: {
                functionName: 'Pipeline.validate',
                errorType: 'Other',
            }
        };
        return [violation];
    }

    public gitValidate(pipeline: Pipeline): IOwnedViolation[] | undefined {
        const violations: IOwnedViolation[] = [];
        const nameViolations = Pipeline.validateNoDuplicateNamedNestedActivities(pipeline);
        nameViolations.forEach((violation) => {
            violations.push({
                owned: pipeline,
                context: violation.context,
                message: violation.message,
                logDetails: violation.logDetails,
                corrected$: of(null),
                attribute: null
            });
        });
        return violations;
    }

    public calcLegacyDependencies(): void {
        for (const activity of this.activities) {
            const act = activity as IExtendedActivity;
            const id = `${this.id}-${act.id}`;
            if (!GlobalVariables.disableNestedDependencyCalculation) {
                if (this._actLegacyMapping.has(id)) {
                    this.hasLegacyDependency = this._actLegacyMapping.get(id);
                } else if (act.pipeline) { // ForEachActivity & UntilActivity
                    this.hasLegacyDependency = this._calcNestedActivityLegacyDependencies(act.pipeline);
                } else if (act.defaultPipeline) { // SwitchActivity
                    this.hasLegacyDependency = this._calcNestedActivityLegacyDependencies(act.defaultPipeline);
                    for (const caseItem of act.cases) {
                        this.hasLegacyDependency = this.hasLegacyDependency || this._calcNestedActivityLegacyDependencies(caseItem.casePipeline);
                    }
                } else if (act.ifFalseActivityPipeline || act.ifTrueActivityPipeline) {  // IfConditionActivity
                    this.hasLegacyDependency = this._calcNestedActivityLegacyDependencies(act.ifFalseActivityPipeline)
                        || this._calcNestedActivityLegacyDependencies(act.ifTrueActivityPipeline);
                }
            }
            this.hasLegacyDependency = this.hasLegacyDependency || this._calcSimpleActivityLegacyDependencies(act);
            this._actLegacyMapping.set(id, this.hasLegacyDependency);
            // If we have a legacy dependency, we can stop
            if (this.hasLegacyDependency) {
                return;
            }
        }
        this.hasLegacyDependency = false;
    }

    private _calcNestedActivityLegacyDependencies(pipeline: Pipeline): boolean {
        pipeline?.calcLegacyDependencies();
        return pipeline?.hasLegacyDependency;
    }

    private _calcSimpleActivityLegacyDependencies(act: IExtendedActivity): boolean {
        if (act.sourceDataset) {
            return act.sourceDataset.hasLegacyDependency;
        }
        if (act.sinkDataset) {
            return act.sinkDataset.hasLegacyDependency;
        }
        if (act.linkedService) {
            return act.linkedService.isLegacyConnector;
        }
        if (act.dataset) {
            return act.dataset.hasLegacyDependency;
        }
        return false;
    }

    private dfsCycleScan(activity: Activity, provider?: (activity: Activity) => Set<Link>): Activity | undefined {
        activity._visited = ActivityScan.SEEN;
        for (const link of (provider ? provider(activity) : activity.toLinks)) {
            // Means we did not start our scan yet on this and it is already gray
            const toActivity = link.toActivity;
            if (toActivity) {
                if (toActivity._visited === ActivityScan.SEEN) {
                    return toActivity;
                }
                // Not yet scanned? Scan and verify if it is indeed no cycles
                if (toActivity._visited === ActivityScan.UNSEEN) {
                    const inCycleActivity = this.dfsCycleScan(toActivity, provider);
                    if (inCycleActivity) {
                        return inCycleActivity;
                    }
                }
            }
        }
        activity._visited = ActivityScan.SCANNED;
        return undefined;
    }
}
